import React, { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import reportSelectors from "redux/app/report/selectors";
import { transformAllVesselsGarbageCategoryDataForTable } from "redux/app/report/utils";
import { IStore } from "redux/store";

const garbageCategoryAllVesselsHeaders = [
	<div>Year</div>,
	<div>Quarter</div>,
	<div>
		<div>CAT A</div> <div> Plastics</div>
	</div>,
	<div>
		<div>CAT B</div> <div>Food waste</div>
	</div>,
	<div>
		<div>CAT C</div> <div>Domestic waste</div>
	</div>,
	<div>
		<div>CAT D</div> <div>Cooking oil</div>
	</div>,
	<div>
		<div>CAT E</div> <div>Ashes</div>
	</div>,
	<div>
		<div>CAT F</div> <div>Operational waste</div>
	</div>,
	<div>
		<div>CAT I</div> <div>Electronic waste</div>
	</div>,
];

type OwnProps = {};
type GarbageCategoryTableProps = PropsFromRedux & OwnProps;
function GarbageCategoryTable(props: GarbageCategoryTableProps) {
	const { garbageCategoryAllVesselsData, year } = props;

	const tableRowRecords = useMemo(() => {
		return (
			transformAllVesselsGarbageCategoryDataForTable(
				garbageCategoryAllVesselsData
			) ?? []
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [garbageCategoryAllVesselsData]);

	// Plain text headers for CSV export
	const csvHeaders = [
		"Year",
		"Quarter",
		"CAT A - Plastics",
		"CAT B - Food waste",
		"CAT C - Domestic waste",
		"CAT D - Cooking oil",
		"CAT E - Ashes",
		"CAT F - Operational waste",
		"CAT I - Electronic waste",
	];

	const handleExportCSV = () => {
		if (!tableRowRecords || !tableRowRecords.length) return;

		try {
			// Generate CSV content
			const rows = tableRowRecords.map((row) => {
				const values = [
					year,
					row.quarter,
					row.A.toFixed(2), // CAT A - Plastics
					row.B.toFixed(2), // CAT B - Food waste
					row.C.toFixed(2), // CAT C - Domestic waste
					row.D.toFixed(2), // CAT D - Cooking oil
					row.E.toFixed(2), // CAT E - Ashes
					row.F.toFixed(2), // CAT F - Operational waste
					row.I.toFixed(2), // CAT I - Electronic waste
				];

				// Handle special characters and format values
				return values
					.map((val) => {
						if (val === null || val === undefined) return "";
						const str = String(val);
						// Escape quotes and wrap in quotes if contains comma or newline
						const escaped = str.replace(/"/g, '""');
						return /[,\n"]/.test(escaped) ? `"${escaped}"` : escaped;
					})
					.join(",");
			});

			// Combine headers and rows
			const csvContent = [csvHeaders.join(","), ...rows].join("\n");

			// Create and trigger download
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);

			// Set filename with current date for better tracking
			const date = new Date().toISOString().split("T")[0];
			const filename = `garbage_category_${date}_${year}.csv`;

			link.setAttribute("href", url);
			link.setAttribute("download", filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error exporting CSV:", error);
			alert("Failed to export data. Please try again.");
		}
	};

	return (
		<div className="my-3">
			<div className="flex justify-end mb-4">
				<button
					onClick={handleExportCSV}
					className="p-3 px-6 py-1 mt-1 mr-1 text-base text-indigo-700 border border-indigo-600 rounded-sm outline-none ws-button-og hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
					disabled={!tableRowRecords || tableRowRecords.length === 0}
					title={
						!tableRowRecords || tableRowRecords.length === 0
							? "No data to export"
							: "Export data as CSV"
					}
				>
					<span className="mr-3 text-sm">Export Data</span>
					<span className="mb-0.5 text-sm bp3-icon bp3-icon-download"></span>
				</button>
			</div>
			<h2 className="text-xl font-medium mb-3">
				Garbage volume by category (m³)
			</h2>{" "}
			<table className="w-full h-full border border-gray-400 table-auto rounded-md font-redhat-text  overflow-auto  border-collapse text-center">
				<thead>
					<tr>
						{garbageCategoryAllVesselsHeaders.map((headerNode, index) => (
							<th
								key={index}
								className="px-3 py-3 text-sm border font-medium leading-4 tracking-wide text-center text-gray-700 bg-gray-100 border-b border-gray-300"
							>
								{headerNode}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{tableRowRecords.map((row, index) => {
						const { quarter, A, B, C, D, E, F, I } = row;
						return (
							<tr key={row.quarter}>
								{index === 0 && (
									<td
										rowSpan={5}
										className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]"
									>
										{year}
									</td>
								)}
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{quarter}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{A.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{B.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{C.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{D.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{E.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{F.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{I.toFixed(2)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const garbageCategoryAllVesselsData =
		reportSelectors.getGarbageCategoryAllVesselsData(reportStore);
	const year = reportSelectors.getGarbageYear(reportStore);

	return {
		garbageCategoryAllVesselsData,
		year,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(GarbageCategoryTable);
